import React from "react";
import { useTranslation } from "react-i18next";

import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";
import FormInput from "shared/components/forms/FormInput";
import FormSelect from "shared/components/forms/FormSelect";
import { ISRAELI_BANKS } from "features/courier-onboarding/utils/bankCodeMapper";

interface Props {
  errors: Record<string, string>;
}

/**
 * Editable bank account fields, rendered inside the review step on the
 * LICENSED_BUSINESS track. Every field can be prefilled from the bank
 * confirmation OCR on the documents step and stays editable here.
 */
const BankAccountFields: React.FC<Props> = ({ errors }) => {
  const { t } = useTranslation();
  const { data, setBankAccount } = useCourierOnboarding();
  const b = data.bankAccountInfo;

  const tError = (key: string | undefined): string | undefined =>
    key ? (t(key) as string) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <FormSelect
        id="field-bankName"
        fieldKey="bankName"
        label={t("onboarding.bankAccount.bankName")}
        placeholder={t("onboarding.bankAccount.bankNamePlaceholder") ?? ""}
        value={b.bankName}
        onChange={(e) => {
          const bankName = e.target.value;
          const bank = ISRAELI_BANKS.find((item) => item.name === bankName);
          setBankAccount({
            bankName,
            bankCode: bank?.code ?? "",
          });
        }}
        options={ISRAELI_BANKS.map((bank) => ({
          value: bank.name,
          label: `${bank.name} - ${bank.code}`,
        }))}
        error={tError(errors.bankName)}
        required
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          id="field-bankBranchNumber"
          fieldKey="bankBranchNumber"
          label={t("onboarding.bankAccount.bankBranchNumber")}
          placeholder={
            t("onboarding.bankAccount.bankBranchNumberPlaceholder") ?? ""
          }
          value={b.bankBranchNumber}
          onChange={(e) =>
            setBankAccount({
              bankBranchNumber: e.target.value.replace(/\D/g, "").slice(0, 6),
            })
          }
          error={tError(errors.bankBranchNumber)}
          inputMode="numeric"
          required
        />
        <FormInput
          id="field-bankAccountNumber"
          fieldKey="bankAccountNumber"
          label={t("onboarding.bankAccount.bankAccountNumber")}
          placeholder={
            t("onboarding.bankAccount.bankAccountNumberPlaceholder") ?? ""
          }
          value={b.bankAccountNumber}
          onChange={(e) =>
            setBankAccount({
              bankAccountNumber: e.target.value.replace(/\D/g, "").slice(0, 12),
            })
          }
          error={tError(errors.bankAccountNumber)}
          inputMode="numeric"
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          id="field-bankAccountOwnerName"
          fieldKey="bankAccountOwnerName"
          label={t("onboarding.bankAccount.bankAccountOwnerName")}
          placeholder={
            t("onboarding.bankAccount.bankAccountOwnerNamePlaceholder") ?? ""
          }
          value={b.bankAccountOwnerName}
          onChange={(e) =>
            setBankAccount({ bankAccountOwnerName: e.target.value })
          }
          error={tError(errors.bankAccountOwnerName)}
          required
        />
        <FormInput
          id="field-bankAccountOwnerId"
          fieldKey="bankAccountOwnerId"
          label={t("onboarding.bankAccount.bankAccountOwnerId")}
          placeholder={
            t("onboarding.bankAccount.bankAccountOwnerIdPlaceholder") ?? ""
          }
          value={b.bankAccountOwnerId}
          onChange={(e) =>
            setBankAccount({
              bankAccountOwnerId: e.target.value.replace(/\D/g, "").slice(0, 9),
            })
          }
          error={tError(errors.bankAccountOwnerId)}
          inputMode="numeric"
          maxLength={9}
          required
        />
      </div>
    </div>
  );
};

export default BankAccountFields;
